/**
 * Shared attendance presentation — stat cards + day-by-day timeline.
 * Used by MyAttendance (own) and hr/EmployeeAttendance (HR picks a staff member).
 * Both pages feed it the same { summary, days } shape from the attendance API.
 */
import { useState } from 'react';
import { currentCyclePeriod, isCalendarMonthCycle, payCycleRange, prevPeriod } from '@payroll/shared';
import type { LeaveProof, MyAttendanceDay, MyAttendanceResp, OtOnDay } from '../../hooks';
import { resolveFileUrl } from '../../lib/api-client';

const KIND_LABEL: Record<string, string> = {
  morning_in: 'เข้า',
  lunch_out: 'พัก',
  lunch_in: 'กลับ',
  evening_out: 'เลิก',
};
const KIND_ORDER = ['morning_in', 'lunch_out', 'lunch_in', 'evening_out'] as const;

/** ป้ายชนิด OT — ต้องตรงกับ OT_TYPE_TH ฝั่ง worker (handlers/approval.ts) */
const OT_TYPE_LABEL: Record<string, string> = {
  ot_1: 'OT วันทำงาน',
  ot_2: 'OT วันหยุด',
  ot_3: 'OT เกินเวลาวันหยุด',
};

/** "18:00" — ตัดวินาทีที่ Postgres ส่งมาเป็น TIME ("18:00:00") ทิ้ง */
const hhmm = (t: string) => (t ?? '').slice(0, 5);

export function thisMonthRange() {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  const yyyy = bkk.getUTCFullYear();
  const mm = String(bkk.getUTCMonth() + 1).padStart(2, '0');
  const today = `${yyyy}-${mm}-${String(bkk.getUTCDate()).padStart(2, '0')}`;
  return { from: `${yyyy}-${mm}-01`, to: today };
}

export function lastMonthRange() {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  const y = bkk.getUTCFullYear();
  const m = bkk.getUTCMonth();
  const lastY = m === 0 ? y - 1 : y;
  const lastM = m === 0 ? 12 : m;
  const mm = String(lastM).padStart(2, '0');
  const lastDay = new Date(Date.UTC(lastY, lastM, 0)).getUTCDate();
  return { from: `${lastY}-${mm}-01`, to: `${lastY}-${mm}-${String(lastDay).padStart(2, '0')}` };
}

/**
 * สัปดาห์ที่ระบุ ตามปฏิทินไทย เริ่มวันจันทร์ จบวันอาทิตย์ (ISO-8601)
 * @param offset 0 = สัปดาห์นี้, -1 = สัปดาห์ที่แล้ว
 *
 * ⭐ "7 วันล่าสุด" ไม่ใช่ "สัปดาห์นี้" — อันแรกเลื่อนตามวันที่กดดู อันนี้ตรึงหัว-ท้าย
 * สัปดาห์ ทำให้ดึงซ้ำวันไหนก็ได้ตัวเลขเดิม (จำเป็นเวลาเอาไปกระทบยอด)
 */
export function weekRange(offset = 0) {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  const dow = bkk.getUTCDay() === 0 ? 7 : bkk.getUTCDay(); // อาทิตย์ = 7
  const monday = new Date(bkk.getTime() - (dow - 1) * 86400_000 + offset * 7 * 86400_000);
  const sunday = new Date(monday.getTime() + 6 * 86400_000);
  return { from: monday.toISOString().slice(0, 10), to: sunday.toISOString().slice(0, 10) };
}

export function last7DaysRange() {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  const to = bkk.toISOString().slice(0, 10);
  const from = new Date(bkk.getTime() - 6 * 86400_000).toISOString().slice(0, 10);
  return { from, to };
}

/** วันที่วันนี้ตามเวลาไทย (ไม่พึ่ง timezone ของเครื่องผู้ใช้) */
export function todayBkk(): string {
  return new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10);
}

/** รอบจ่ายที่กำลังเดินอยู่ เช่น 26 ส.ค. – 25 ก.ย. (บริษัทที่ปิดงวดไม่ตรงสิ้นเดือน) */
export function currentCycleRange(cutoffDay: number) {
  return payCycleRange(cutoffDay, currentCyclePeriod(cutoffDay, todayBkk()));
}

/** รอบจ่ายก่อนหน้า — รอบที่ปิดไปแล้วล่าสุด (ตัวเลขนิ่งแล้ว ใช้กระทบยอดได้) */
export function lastCycleRange(cutoffDay: number) {
  return payCycleRange(cutoffDay, prevPeriod(currentCyclePeriod(cutoffDay, todayBkk())));
}

export type RangePreset =
  | 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'last-7' | 'cycle' | 'last-cycle' | 'custom';

/**
 * @param cutoffDay วันปิดงวดของบริษัท — จำเป็นเฉพาะ preset 'cycle'/'last-cycle'
 *                  (ไม่ส่งมา = ถอยไปใช้เดือนปฏิทิน ไม่พังแต่ได้ช่วงผิดความตั้งใจ)
 */
export function rangeForPreset(p: RangePreset, cutoffDay?: number) {
  if (p === 'cycle') return cutoffDay ? currentCycleRange(cutoffDay) : thisMonthRange();
  if (p === 'last-cycle') return cutoffDay ? lastCycleRange(cutoffDay) : lastMonthRange();
  if (p === 'last-month') return lastMonthRange();
  if (p === 'this-week') return weekRange(0);
  if (p === 'last-week') return weekRange(-1);
  if (p === 'last-7') return last7DaysRange();
  return thisMonthRange(); // 'this-month' และ 'custom' (custom ให้ผู้ใช้แก้ต่อเอง)
}

/** "26 ส.ค. – 25 ก.ย. 2569" — ป้ายบอกช่วงที่กำลังดูอยู่ */
export function rangeLabel(from: string, to: string): string {
  const d = (iso: string, withYear: boolean) =>
    new Intl.DateTimeFormat('th-TH', {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {}),
      timeZone: 'Asia/Bangkok',
    }).format(new Date(`${iso}T00:00:00+07:00`));
  return `${d(from, false)} – ${d(to, true)}`;
}

/**
 * แถบเลือกช่วงเวลา. ปุ่มรอบจ่าย (26 ชน 25) จะโผล่ก็ต่อเมื่อส่ง `cutoffDay` มา และ
 * ช่องกำหนดวันเองจะโผล่ก็ต่อเมื่อส่ง `onRangeChange` มา — หน้าพนักงาน (MyAttendance)
 * ไม่ส่งทั้งคู่ จึงเห็นปุ่มเท่าเดิมทุกอย่าง
 */
export function AttendanceRangePresets({
  preset,
  onChange,
  cutoffDay,
  range,
  onRangeChange,
}: {
  preset: RangePreset;
  onChange: (p: RangePreset) => void;
  cutoffDay?: number;
  range?: { from: string; to: string };
  onRangeChange?: (r: { from: string; to: string }) => void;
}) {
  const cycleChips =
    cutoffDay && !isCalendarMonthCycle(cutoffDay, todayBkk().slice(0, 7))
      ? ([
          { id: 'cycle', label: `รอบจ่ายนี้ (${cycleChipLabel(cutoffDay)})` },
          { id: 'last-cycle', label: 'รอบจ่ายที่แล้ว' },
        ] as const)
      : ([] as const);

  return (
    <div className="mb-3">
      <div className="flex gap-1 overflow-x-auto">
        {([
          ...cycleChips,
          { id: 'this-week', label: 'สัปดาห์นี้' },
          { id: 'last-week', label: 'สัปดาห์ที่แล้ว' },
          { id: 'this-month', label: 'เดือนนี้' },
          { id: 'last-month', label: 'เดือนที่แล้ว' },
          { id: 'last-7', label: '7 วันล่าสุด' },
          ...(onRangeChange ? ([{ id: 'custom', label: 'กำหนดเอง' }] as const) : ([] as const)),
        ] as ReadonlyArray<{ id: RangePreset; label: string }>).map((p) => (
          <button
            key={p.id}
            onClick={() => onChange(p.id)}
            className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
              preset === p.id ? 'bg-brand-100 text-brand-700 font-medium' : 'bg-white border border-gray-200 text-gray-600'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {preset === 'custom' && onRangeChange && range && (
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <label className="flex-1">
            <span className="label">ตั้งแต่วันที่</span>
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e) => e.target.value && onRangeChange({ ...range, from: e.target.value })}
              className="input w-full"
            />
          </label>
          <label className="flex-1">
            <span className="label">ถึงวันที่</span>
            <input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(e) => e.target.value && onRangeChange({ ...range, to: e.target.value })}
              className="input w-full"
            />
          </label>
        </div>
      )}

      {range && (
        <p className="text-xs text-gray-500 mt-1.5">ช่วงที่ดูอยู่: {rangeLabel(range.from, range.to)}</p>
      )}
    </div>
  );
}

/** "26–25" — เลขวันเริ่มกับวันปิดของรอบ ไว้ติดบนปุ่มให้ HR รู้ทันทีว่าเป็นรอบไหน */
function cycleChipLabel(cutoffDay: number): string {
  const { from, to } = currentCycleRange(cutoffDay);
  return `${Number(from.slice(8, 10))}–${Number(to.slice(8, 10))}`;
}

function formatThaiDate(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Bangkok',
  }).format(new Date(`${iso}T00:00:00+07:00`));
}

// captured_at เป็น UTC ISO → ต้องแปลงเป็นเวลาไทยด้วย Intl (slice ตรงๆ จะเพี้ยน -7 ชม.)
function formatBkkTime(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Bangkok',
  }).format(new Date(iso));
}

function StatusBadge({ status }: { status: MyAttendanceDay['status'] }) {
  const map: Record<MyAttendanceDay['status'], { label: string; cls: string }> = {
    working: { label: 'ทำงาน', cls: 'bg-emerald-100 text-emerald-700' },
    short_work: { label: 'งานยังไม่ครบ', cls: 'bg-amber-100 text-amber-700' },
    on_leave: { label: 'ลา', cls: 'bg-blue-100 text-blue-700' },
    absent: { label: 'ขาด', cls: 'bg-red-100 text-red-700' },
    holiday: { label: 'หยุด', cls: 'bg-gray-100 text-gray-600' },
    future: { label: 'ยังไม่ถึง', cls: 'bg-gray-50 text-gray-400' },
  };
  const m = map[status];
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}

/** ป้ายชนิดลา — ตรงกับ LEAVE_TYPE_TH ฝั่ง worker */
const LEAVE_TYPE_LABEL: Record<string, string> = {
  sick: 'ลาป่วย',
  personal: 'ลากิจ',
  vacation: 'ลาพักร้อน',
  unpaid: 'ลาไม่รับค่าจ้าง',
  maternity: 'ลาคลอด',
};
export const leaveLabel = (t: string) => LEAVE_TYPE_LABEL[t] ?? `ลา ${t}`;

/** "ครึ่งวัน" / "0.25 วัน" — เศษวันที่ลา (เต็มวันไม่ต้องบอก) */
function leaveFracLabel(days: number): string {
  if (days >= 1) return '';
  if (days === 0.5) return 'ครึ่งวัน';
  return `${days} วัน`;
}

/** "18 ก.ย. 09:12" เวลาไทย */
function formatBkkDateTime(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Bangkok',
  }).format(new Date(iso));
}

/** fb#276 — หลักฐานใบลาที่อนุมัติแล้ว ให้ HR เรียกดูย้อนหลังได้ทุกใบ รวมใบที่ถูกยกเลิกไปแล้ว */
function LeaveProofBox({ p }: { p: LeaveProof }) {
  const cancelled = p.status === 'cancelled';
  const file = resolveFileUrl(p.evidence_url);
  return (
    <div className={`text-xs rounded-lg border p-2 mb-1 ${cancelled ? 'bg-gray-50 border-gray-200 text-gray-600' : 'bg-blue-50/50 border-blue-100 text-gray-700'}`}>
      <p className="font-medium">
        {cancelled ? 'ใบลาที่ถูกยกเลิกแล้ว' : 'หลักฐานใบลา'}
        {p.leave_id && <span className="font-normal text-gray-500"> · {p.leave_id}</span>}
      </p>
      {cancelled && <p>{leaveLabel(p.type)}: {p.reason ?? '-'}</p>}
      {p.submitted_at && !p.imported_at && <p>ยื่นเมื่อ {formatBkkDateTime(p.submitted_at)}</p>}
      {p.approvals.map((a) => (
        <p key={a.level}>
          {p.status === 'conditionally_approved' ? 'อนุมัติแบบมีเงื่อนไข' : 'อนุมัติ'}
          {p.approvals.length > 1 ? ` ขั้น ${a.level}` : ''} โดย {a.name ?? 'ไม่ทราบชื่อ'} · {formatBkkDateTime(a.at)}
        </p>
      ))}
      {p.imported_at && (
        <p>นำเข้าจากระบบเดิม (อนุมัติในระบบเดิมแล้ว){p.imported_at ? ` · นำเข้าเมื่อ ${formatBkkDateTime(p.imported_at)}` : ''}</p>
      )}
      {cancelled && p.cancelled && (
        <p className="text-red-700">
          ยกเลิกโดย {p.cancelled.name ?? 'ไม่ทราบชื่อ'}
          {p.cancelled.at ? ` · ${formatBkkDateTime(p.cancelled.at)}` : ''}
          {p.cancelled.reason ? ` · เหตุผล: ${p.cancelled.reason}` : ''}
        </p>
      )}
      {file && (
        <a href={file} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-blue-700 underline">
          ดูไฟล์แนบ
        </a>
      )}
    </div>
  );
}

/** HR กด "มาทำงานจริง" บนวันที่ลาเต็มวันแต่มีสแกน → ยกเลิกใบลาวันนั้น (ส่งมาเฉพาะหน้า HR) */
export type VoidLeaveHandler = (day: MyAttendanceDay) => void;

/** fb#113 — HR/OWNER ยกเลิกใบลาที่อนุมัติแล้วของวันที่ผ่านมา (ไม่ต้องมีสแกน) ให้พนักงานยื่นใหม่ · คืน true = สำเร็จ */
export type CancelLeaveHandler = (day: MyAttendanceDay, reason: string) => Promise<boolean>;

function LeaveCancelLine({ day, onCancelLeave }: { day: MyAttendanceDay; onCancelLeave: CancelLeaveHandler }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (await onCancelLeave(day, reason.trim())) { setOpen(false); setReason(''); }
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button type="button" className="text-xs underline text-red-700 min-h-[32px] mb-1" onClick={() => setOpen(true)}>
        ยกเลิกใบลานี้ (ให้พนักงานยื่นใหม่)
      </button>
    );
  }
  return (
    <div className="mt-1 mb-1 text-xs bg-red-50 border border-red-200 rounded-lg p-2 space-y-2">
      <p className="font-medium text-red-900">ยกเลิกใบ{leaveLabel(day.leave!.type)} วันที่ {day.date}</p>
      <p className="text-gray-600">ใช้เมื่อใบลาผิดประเภท เช่น เลือกลาไม่รับเงินเดือนทั้งที่มีสิทธิ์ลาอื่น — ยกเลิกแล้วพนักงานยื่นใหม่ได้ ถ้าไม่ยื่นและไม่มีสแกน วันนี้จะนับเป็นขาดงาน</p>
      <label className="flex flex-col">เหตุผล (บันทึกในประวัติ + แจ้งพนักงาน)
        <input type="text" className="input text-base" value={reason} maxLength={500} placeholder="เช่น เลือกประเภทลาผิด ให้ยื่นเป็นลากิจ" onChange={(e) => setReason(e.target.value)} />
      </label>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700" disabled={busy} onClick={() => setOpen(false)}>ปิด</button>
        <button type="button" className="px-3 py-2 rounded-lg bg-red-600 text-white font-medium" disabled={busy} onClick={submit}>
          {busy ? 'กำลังยกเลิก…' : 'ยกเลิกใบลานี้'}
        </button>
      </div>
    </div>
  );
}

/** fb#83 — HR แก้/ยกเลิก OT ที่อนุมัติผิด (ส่งมาเฉพาะหน้า HR) · คืน true = สำเร็จ ปิดฟอร์ม */
export type AmendOtInput = { action: 'amend' | 'void'; start_time: string; end_time: string; reason: string };
export type AmendOtHandler = (day: MyAttendanceDay, ot: OtOnDay, input: AmendOtInput) => Promise<boolean>;

const toMin = (t: string) => { const [h, m] = hhmm(t).split(':').map(Number); return (h ?? 0) * 60 + (m ?? 0); };

/** OT วันทำงานที่เริ่มก่อนเลิกกะ (เช่น 09:00–18:30 บนกะ 09:00–18:00) — มักเป็นการกรอกผิด */
function otOverlapsShift(day: MyAttendanceDay, o: OtOnDay): boolean {
  if (o.ot_type !== 'ot_1' || !day.shift_start || !day.shift_end) return false;
  const sS = toMin(day.shift_start);
  const sE = toMin(day.shift_end);
  if (sE <= sS) return false; // กะข้ามคืน — ไม่เดา
  const oS = toMin(o.start_time);
  const oE = toMin(o.end_time) <= oS ? 1440 : toMin(o.end_time);
  return oS < sE && oE > sS;
}

function OtLine({ day, o, onAmendOt }: { day: MyAttendanceDay; o: OtOnDay; onAmendOt?: AmendOtHandler }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(hhmm(o.start_time));
  const [end, setEnd] = useState(hhmm(o.end_time));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const overlap = otOverlapsShift(day, o);
  const shiftEnd = day.shift_end ? hhmm(day.shift_end) : null;
  const submit = async (action: 'amend' | 'void') => {
    if (busy) return;
    setBusy(true);
    try {
      const ok = await onAmendOt!(day, o, { action, start_time: start, end_time: end, reason: reason.trim() });
      if (ok) setOpen(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="text-xs text-violet-700 flex flex-wrap items-center gap-x-2">
        <span>{OT_TYPE_LABEL[o.ot_type] ?? o.ot_type} {hhmm(o.start_time)}–{hhmm(o.end_time)} ({o.hours} ชม.)</span>
        {onAmendOt && !open && (
          <button type="button" className="underline text-violet-800 min-h-[32px]" onClick={() => setOpen(true)}>
            แก้ OT
          </button>
        )}
      </p>
      {onAmendOt && overlap && (
        <p className="text-xs text-amber-800">ระวัง: OT ทับเวลากะ ({hhmm(day.shift_start!)}–{shiftEnd}) — ถ้าอนุมัติผิด กด "แก้ OT"</p>
      )}
      {open && (
        <div className="mt-1.5 text-xs bg-violet-50 border border-violet-200 rounded-lg p-2 space-y-2">
          <p className="font-medium text-violet-900">แก้ OT ที่อนุมัติแล้ว</p>
          <p className="text-gray-600">ลดช่วงเวลาได้ภายใน {hhmm(o.start_time)}–{hhmm(o.end_time)} เท่านั้น · ถ้าทำ OT มากกว่านี้ให้พนักงานยื่นใบใหม่</p>
          <div className="flex items-center gap-2">
            <label className="flex flex-col">เริ่ม
              <input type="time" className="input text-base" value={start} onChange={(e) => setStart(e.target.value)} />
            </label>
            <label className="flex flex-col">ถึง
              <input type="time" className="input text-base" value={end} onChange={(e) => setEnd(e.target.value)} />
            </label>
          </div>
          {overlap && shiftEnd && start !== shiftEnd && (
            <button type="button" className="underline text-violet-800" onClick={() => setStart(shiftEnd)}>
              ให้เริ่มหลังเลิกกะ ({shiftEnd})
            </button>
          )}
          <label className="flex flex-col">เหตุผล (บันทึกในประวัติ + แจ้งพนักงาน)
            <input type="text" className="input text-base" value={reason} maxLength={500} placeholder="เช่น อนุมัติผิด ขอจริง 18:00–18:30" onChange={(e) => setReason(e.target.value)} />
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700" disabled={busy} onClick={() => setOpen(false)}>ปิด</button>
            <button type="button" className="px-3 py-2 rounded-lg border border-red-300 text-red-700" disabled={busy} onClick={() => submit('void')}>ยกเลิกทั้งใบ</button>
            <button type="button" className="px-3 py-2 rounded-lg bg-violet-700 text-white font-medium" disabled={busy} onClick={() => submit('amend')}>บันทึกเวลาใหม่</button>
          </div>
        </div>
      )}
    </div>
  );
}

function DayRow({ day, onVoidLeave, onCancelLeave, onAmendOt }: { day: MyAttendanceDay; onVoidLeave?: VoidLeaveHandler; onCancelLeave?: CancelLeaveHandler; onAmendOt?: AmendOtHandler }) {
  const hasEvents = Object.keys(day.events).length > 0;
  const [expandedKind, setExpandedKind] = useState<string | null>(null);
  const selfiesAvailable = KIND_ORDER.filter((k) => day.events[k]?.selfie_path);

  return (
    <div className="card p-3">
      <div className="flex justify-between items-start mb-1">
        <span className="font-medium text-sm">{formatThaiDate(day.date)}</span>
        <StatusBadge status={day.status} />
      </div>
      {day.shift_code && (
        <p className="text-xs text-gray-500 mb-2">
          {day.shift_code} ({day.shift_start}-{day.shift_end})
          {day.shift_time_changed && <span className="badge-info ml-1 whitespace-nowrap">เปลี่ยนเวลา (อนุมัติแล้ว)</span>}
        </p>
      )}
      {day.holiday && <p className="text-xs text-gray-500 mb-1">วันหยุด: {day.holiday}</p>}
      {day.leave && (
        <p className="text-xs text-blue-700 mb-1">
          {leaveLabel(day.leave.type)}{day.leave.days < 1 ? ` ${leaveFracLabel(day.leave.days)}` : ''}: {day.leave.reason ?? '-'}
          {/* fb#71 — ลาบางส่วนแล้วมาสแกน: บอกตรง ๆ ว่าส่วนที่มาถูกนับเป็นวันทำงานเท่าไหร่ (ตรงกับที่คิดเงิน) */}
          {day.leave.worked > 0 && <span className="text-emerald-700"> · มาทำงาน {day.leave.worked} วัน</span>}
        </p>
      )}
      {day.leave_proof?.map((p) => <LeaveProofBox key={p.id} p={p} />)}
      {/* fb#113 — ใบลาที่อนุมัติแล้วของวันที่ผ่านมา ยกเลิกได้จากแถววันนี้เลย (เดิมซ่อนอยู่ที่ ตั้งค่าสิทธิ์การลา → ดูรายละเอียด) */}
      {onCancelLeave && day.leave?.leave_id && !day.leave.scan_conflict && day.date <= todayBkk() && (
        <LeaveCancelLine day={day} onCancelLeave={onCancelLeave} />
      )}
      {day.leave?.scan_conflict && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2 mb-1">
          <p className="font-medium">ลาเต็มวัน แต่มีสแกนเข้างานวันนี้</p>
          <p className="mt-0.5">
            ระบบยังนับวันนี้เป็น "ลา" (ไม่ใช่วันทำงาน) และหักสิทธิ์ลาไว้ — ถ้ามาทำงานจริง ให้ยกเลิกใบลาวันนี้
          </p>
          {onVoidLeave && (
            <button
              type="button"
              className="mt-1.5 px-3 py-1 rounded-lg bg-amber-600 text-white text-xs font-medium"
              onClick={() => onVoidLeave(day)}
            >
              มาทำงานจริง — ยกเลิกใบลาวันนี้
            </button>
          )}
        </div>
      )}
      {day.ot?.length > 0 && (
        <div className="mb-1 space-y-0.5">
          {day.ot.map((o, i) => (
            <OtLine key={o.id ?? `${o.start_time}-${i}`} day={day} o={o} onAmendOt={onAmendOt} />
          ))}
        </div>
      )}
      {hasEvents && (
        <div className="flex gap-3 text-xs text-gray-700">
          {KIND_ORDER.map((k) => {
            const ev = day.events[k];
            if (!ev) return <span key={k} className="text-gray-300">{KIND_LABEL[k]} —</span>;
            const late = ev.late_minutes > 0;
            const hasPic = !!ev.selfie_path;
            return (
              <button
                key={k}
                type="button"
                onClick={() => hasPic && setExpandedKind(expandedKind === k ? null : k)}
                disabled={!hasPic}
                className={`${late ? 'text-amber-700' : 'text-emerald-700'} ${hasPic ? 'underline decoration-dotted' : ''}`}
              >
                {KIND_LABEL[k]} {formatBkkTime(ev.at)}
                {late ? ` (+${ev.late_minutes}น.)` : ''}
              </button>
            );
          })}
        </div>
      )}
      {expandedKind && day.events[expandedKind]?.selfie_path && (
        <div className="mt-2">
          <img
            src={resolveFileUrl(day.events[expandedKind].selfie_path!) ?? ''}
            alt={`${KIND_LABEL[expandedKind]} ${day.date}`}
            className="w-full max-w-xs rounded-lg border border-gray-200"
            loading="lazy"
          />
        </div>
      )}
      {hasEvents && !expandedKind && selfiesAvailable.length > 0 && (
        <p className="text-[10px] text-gray-400 mt-1">📷 แตะที่เวลาเพื่อดูรูป</p>
      )}
    </div>
  );
}

function formatThaiDateFull(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Bangkok',
  }).format(new Date(`${iso}T00:00:00+07:00`));
}

/** มีสแกนแต่อยู่ก่อนวันเริ่มงาน / หลังวันสิ้นสุดตามประวัติ — ระบบข้ามวันพวกนี้ทั้งจอและเงิน
 *  ต้องบอกตรง ๆ ไม่งั้น HR เห็นแค่ "ไม่มีข้อมูล" แล้วคิดว่าระบบพัง (TN EMP0060) */
function EmploymentRangeWarning({ emp }: { emp: NonNullable<MyAttendanceResp['employment']> }) {
  if (!emp.scan_days_outside) return null;
  const range =
    emp.first_scan_outside && emp.last_scan_outside && emp.first_scan_outside !== emp.last_scan_outside
      ? `${formatThaiDateFull(emp.first_scan_outside)} – ${formatThaiDateFull(emp.last_scan_outside)}`
      : emp.first_scan_outside ? formatThaiDateFull(emp.first_scan_outside) : '';
  const bound = [
    emp.start_date ? `วันเริ่มงาน ${formatThaiDateFull(emp.start_date)}` : null,
    emp.end_date ? `วันสิ้นสุด ${formatThaiDateFull(emp.end_date)}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <div className="card p-3 mb-3 border border-amber-300 bg-amber-50 text-amber-900">
      <p className="text-sm font-semibold">⚠ มีการสแกน {emp.scan_days_outside} วันที่อยู่นอกช่วงทำงานตามประวัติพนักงาน</p>
      <p className="text-xs mt-1">
        ประวัติพนักงานตั้งไว้: {bound || '-'} · วันที่สแกนนอกช่วง: {range}
      </p>
      <p className="text-xs mt-1">
        วันเหล่านี้ระบบไม่นับเป็นวันทำงานและไม่คิดเงิน — ถ้าวันเริ่มงานตั้งผิด (เช่น เลือกปีผิด)
        ให้แก้ที่หน้าพนักงาน แล้วกด "เริ่มสรุปเวลา" ของงวดนั้นใหม่
      </p>
    </div>
  );
}

export function AttendanceTimeline({
  data,
  isLoading,
  onVoidLeave,
  onCancelLeave,
  onAmendOt,
}: {
  data: MyAttendanceResp | undefined;
  isLoading: boolean;
  /** เฉพาะหน้า HR — ปุ่มยกเลิกใบลาของวันที่ลาเต็มวันแต่มาสแกน (fb#71) */
  onVoidLeave?: VoidLeaveHandler;
  /** เฉพาะ HR/OWNER — ยกเลิกใบลาที่อนุมัติแล้วของวันที่ผ่านมา ให้ยื่นใหม่ (fb#113) */
  onCancelLeave?: CancelLeaveHandler;
  /** เฉพาะ HR/OWNER — แก้/ยกเลิก OT ที่อนุมัติผิด (fb#83) */
  onAmendOt?: AmendOtHandler;
}) {
  const conflictDates = data?.summary.leave_scan_dates ?? [];
  return (
    <>
      {data?.employment && <EmploymentRangeWarning emp={data.employment} />}
      {conflictDates.length > 0 && (
        <div className="card p-3 mb-3 border border-amber-300 bg-amber-50 text-amber-900">
          <p className="text-sm font-semibold">⚠ ลาเต็มวันแต่มีสแกนเข้างาน {conflictDates.length} วัน</p>
          <p className="text-xs mt-1">{conflictDates.map(formatThaiDateFull).join(' · ')}</p>
          <p className="text-xs mt-1">
            วันเหล่านี้ยังนับเป็น "วันลา" ไม่ใช่วันทำงาน และถูกหักสิทธิ์ลาไว้ — ถ้ามาทำงานจริง
            {onVoidLeave ? ' กดปุ่ม "มาทำงานจริง" ในแถววันนั้น' : ' แจ้งฝ่ายบุคคลให้ยกเลิกใบลา'} แล้วกด "เริ่มสรุปเวลา" ของงวดนั้นใหม่
          </p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="card p-3">
          <p className="text-xs text-gray-500">วันทำงาน</p>
          <p className="text-xl font-bold text-emerald-700">{data?.summary.working_days ?? '-'}</p>
          {/* fb#71 — วันลาครึ่งวันที่มาสแกน นับส่วนที่มาเป็น .5 (ตรงกับที่คิดเงิน) */}
          <p className="text-[10px] text-gray-400">รวมส่วนที่มาทำงานของวันลาครึ่งวัน</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">วันลา</p>
          <p className="text-xl font-bold text-blue-700">{data?.summary.leave_days ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">วันขาด</p>
          <p className="text-xl font-bold text-red-700">{data?.summary.absent_days ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">สาย (นาที)</p>
          <p className="text-xl font-bold text-amber-700">{data?.summary.total_late_minutes ?? '-'}</p>
        </div>
        <div className="card p-3 col-span-2">
          <p className="text-xs text-gray-500">
            OT ที่อนุมัติ (ชม.)
            {data?.summary.ot_count ? ` · ${data.summary.ot_count} ใบ` : ''}
          </p>
          <p className="text-xl font-bold text-violet-700">{data?.summary.ot_hours ?? '-'}</p>
          {/* ⚠️ ต้องเขียนไว้ตรงนี้ และต้องตรงกับที่ระบบทำจริง — ห้ามอ้างว่า "ถูกตัดก่อนจ่าย"
              เพราะ reconcile บวกชั่วโมงจากใบตรง ๆ (เคยเขียนผิดแบบนั้นมาแล้ว) */}
          <p className="text-[10px] text-gray-400 mt-0.5">
            ชั่วโมงรวมจากใบที่อนุมัติ ปกติตรงกับที่ระบบสรุปไว้ตอนคิดเงิน — ยกเว้นค่าทำงานวันหยุด
            ที่ระบบคิดให้เองจากการสแกนโดยไม่ต้องมีใบ · คนโหมดเงินเดือนล้วนและรายชั่วโมงจะมีชั่วโมงขึ้น
            แต่ไม่ได้รับเงิน OT
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-3">
              <div className="h-4 bg-gray-100 rounded w-1/3 animate-pulse mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3 animate-pulse" />
            </div>
          ))}
        </div>
      )}
      {!isLoading && data && (
        <div className="space-y-2">
          {data.days.length === 0 ? (
            <div className="card p-6 text-center text-gray-400">
              <p className="text-4xl mb-2">📅</p>
              <p className="text-sm">ไม่มีข้อมูลในช่วงนี้</p>
            </div>
          ) : (
            [...data.days].reverse().map((d) => <DayRow key={d.date} day={d} onVoidLeave={onVoidLeave} onCancelLeave={onCancelLeave} onAmendOt={onAmendOt} />)
          )}
        </div>
      )}
    </>
  );
}
